import { useCallback, useEffect, useState } from "react";
import type { FanTopicDto } from "../../../api/types";
import { getUserFacingMessage, showAlert } from "../../../api/errors";
import {
  deleteFanTopic,
  fetchFanTopicsForPlatform,
  patchFanTopic,
  postFanTopic,
} from "../../../api/fanTopicsAdmin";

export default function FanTopicsManager() {
  const [topics, setTopics] = useState<FanTopicDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("from-red-500 to-red-400");
  const [submitBusy, setSubmitBusy] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchFanTopicsForPlatform();
      setTopics(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(getUserFacingMessage(e, "话题列表加载失败。"));
      setTopics([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      showAlert("请输入话题名称");
      return;
    }
    setSubmitBusy(true);
    try {
      await postFanTopic({ name, colorGradient: newColor.trim() || undefined });
      setNewName("");
      await reload();
    } catch (e) {
      showAlert(getUserFacingMessage(e, "创建失败"));
    } finally {
      setSubmitBusy(false);
    }
  };

  const startEdit = (t: FanTopicDto) => {
    setEditingId(t.id);
    setEditName(t.name);
    setEditColor(t.color);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditColor("");
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    const name = editName.trim();
    if (!name) {
      showAlert("名称不能为空");
      return;
    }
    try {
      await patchFanTopic(editingId, {
        name,
        colorGradient: editColor.trim() || undefined,
      });
      cancelEdit();
      await reload();
    } catch (e) {
      showAlert(getUserFacingMessage(e, "保存失败"));
    }
  };

  const handleDelete = async (t: FanTopicDto) => {
    if (!window.confirm(`删除话题「${t.name}」？该话题下帖子会迁移到其它默认话题。`)) return;
    try {
      await deleteFanTopic(t.id);
      await reload();
    } catch (e) {
      showAlert(getUserFacingMessage(e, "删除失败"));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">球迷互动 · 话题</h2>
          <p className="text-xs text-gray-400 mt-0.5">全平台公域话题，会员端发帖与列表均使用此处配置（与租户无关）</p>
        </div>
        <button
          type="button"
          onClick={() => void reload()}
          className="text-xs px-3 py-1.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer"
        >
          <i className="ri-refresh-line mr-1"></i>刷新
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <i className="ri-add-circle-line text-amber-500"></i> 新建话题
        </h3>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-500 mb-1">名称</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="例如：赛事预测"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
            />
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs text-gray-500 mb-1">渐变色（Tailwind 类，可选）</label>
            <input
              type="text"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              placeholder="from-red-500 to-orange-500"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-amber-400 font-mono text-xs"
            />
          </div>
          <button
            type="button"
            disabled={submitBusy}
            onClick={() => void handleCreate()}
            className="px-4 py-2 bg-amber-500 text-white text-sm rounded-xl hover:bg-amber-400 disabled:opacity-50 cursor-pointer"
          >
            {submitBusy ? "提交中…" : "创建"}
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-gray-400 mb-3">加载中…</p>}
      {error && <p className="text-sm text-red-500 mb-3">{error}</p>}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wide">
              <th className="px-4 py-3 font-medium">ID</th>
              <th className="px-4 py-3 font-medium">名称</th>
              <th className="px-4 py-3 font-medium">帖子数</th>
              <th className="px-4 py-3 font-medium">样式</th>
              <th className="px-4 py-3 font-medium text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {topics.map((t) => (
              <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50/80">
                <td className="px-4 py-3 text-gray-500 font-mono">{t.id}</td>
                <td className="px-4 py-3">
                  {editingId === t.id ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full max-w-xs border border-amber-200 rounded-lg px-2 py-1 text-sm"
                    />
                  ) : (
                    <span className="font-medium text-gray-800">{t.name}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{t.count}</td>
                <td className="px-4 py-3">
                  {editingId === t.id ? (
                    <input
                      type="text"
                      value={editColor}
                      onChange={(e) => setEditColor(e.target.value)}
                      className="w-full max-w-md border border-amber-200 rounded-lg px-2 py-1 text-xs font-mono"
                    />
                  ) : (
                    <code className="text-xs text-gray-600 break-all max-w-xs inline-block" title={t.color}>
                      {t.color}
                    </code>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {editingId === t.id ? (
                    <span className="inline-flex gap-2">
                      <button
                        type="button"
                        onClick={() => void saveEdit()}
                        className="text-amber-600 hover:text-amber-700 text-xs cursor-pointer"
                      >
                        保存
                      </button>
                      <button type="button" onClick={cancelEdit} className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer">
                        取消
                      </button>
                    </span>
                  ) : (
                    <span className="inline-flex gap-3">
                      <button
                        type="button"
                        onClick={() => startEdit(t)}
                        className="text-amber-600 hover:text-amber-700 text-xs cursor-pointer"
                      >
                        编辑
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(t)}
                        className="text-red-500 hover:text-red-600 text-xs cursor-pointer"
                      >
                        删除
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {topics.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-gray-400 text-sm">
                  暂无话题，请在上方创建。
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
